/**
 * Retry predicates for the global TanStack Query client.
 *
 * Queries and mutations get deliberately different policies:
 *
 * - **Queries** are idempotent, so retrying is cheap and safe. We retry
 *   anything except a definitive 4xx (the identical request cannot succeed).
 *   Client-side 408 timeouts are retried once — the GET never completed from
 *   our perspective, and repeating it cannot duplicate a write.
 * - **Mutations** are not idempotent. A retry can only be justified when we
 *   are confident the write never reached the application — a connection
 *   failure or a gateway rejection. Anything else (including 5xx, which may
 *   have partially applied) is left for the caller to surface, because
 *   silently re-sending risks duplicate records.
 *
 * Both cap at a single retry.
 */

import { isBffError } from "@/lib/utils/bff-fetch";

/** Maximum retry attempts after the initial request. */
export const MAX_RETRIES = 1;

/**
 * `BffError.status` for connection failures that never produced a server
 * response (`Failed to fetch` / `NetworkError`). Parse errors and other
 * unexpected failures after a response was received must not use this
 * value — mutations treat it as safe to re-send.
 */
export const NETWORK_ERROR_STATUS = 0;

/** Client-side AbortController timeout from `bffFetch`. */
export const CLIENT_TIMEOUT_STATUS = 408;

/**
 * Gateway-level statuses where the request was rejected before the
 * application handled it, so re-sending cannot duplicate a write.
 */
const TRANSIENT_GATEWAY_STATUSES = new Set([502, 503, 504]);

function isDefinitiveClientError(status: number): boolean {
  return status >= 400 && status < 500 && status !== CLIENT_TIMEOUT_STATUS;
}

/**
 * True when the failure is safe to re-send even for a non-idempotent call:
 * the request either never left the browser or was refused by the gateway.
 *
 * Note that 408 (our client-side timeout) is excluded on purpose — the
 * request may well have reached the server and completed.
 */
export function isTransientTransportFailure(status: number): boolean {
  return (
    status === NETWORK_ERROR_STATUS || TRANSIENT_GATEWAY_STATUSES.has(status)
  );
}

/**
 * Retry policy for queries: everything except a definitive 4xx.
 *
 * 401 in particular is definitive — `backendFetch` already attempted a silent
 * refresh server-side, so a client retry only doubles the noise while the
 * global `onError` handler redirects to sign-in.
 *
 * 408 is our own timeout, not a server refusal. Queries retry it once.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (isBffError(error) && isDefinitiveClientError(error.status)) return false;
  return true;
}

/**
 * Retry policy for mutations: only transient transport failures.
 *
 * Errors that are not a `BffError` (for example a `ValidationError` thrown by
 * a Server Action) are never retried — they carry no transport signal, and
 * re-invoking the action would repeat whatever it already did.
 */
export function shouldRetryMutation(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (!isBffError(error)) return false;
  return isTransientTransportFailure(error.status);
}

/** Exponential backoff shared by both policies. */
export function retryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
}
